import classNames from 'classnames';
import { type ReactNode, useLayoutEffect, useRef } from 'react';

/**
 * Number pop-in (transitions.dev): a digit re-enters with a blurred rise when it changes. Only the
 * changed digits move — on a ticking countdown, re-animating the whole number every second is
 * noise.
 */
export const DigitPopIn = ({
  value,
  className,
}: {
  readonly value: string;
  readonly className?: string;
}) => {
  const groupRef = useRef<HTMLSpanElement>(null);
  const previousValue = useRef(value);
  const chars = [...value];
  const previousChars = [...previousValue.current];
  // Aligned from the right, so 10 → 9 compares the ones digits.
  const isChanged = (index: number) =>
    previousValue.current !== value &&
    previousChars[previousChars.length - (chars.length - index)] !== chars[index];

  useLayoutEffect(() => {
    // eslint-disable-next-line @silverhand/fp/no-mutation
    previousValue.current = value;
  });

  useLayoutEffect(() => {
    const group = groupRef.current;

    if (!group) {
      return;
    }

    group.classList.remove('is-animating');
    // Forces a reflow so re-adding the class restarts the keyframes.
    void group.offsetHeight;
    group.classList.add('is-animating');
  }, [value]);

  // Screen readers read each digit span as its own word ("5… 8"), so they get the whole value.
  return (
    <>
      <span
        ref={groupRef}
        aria-hidden
        className={classNames('t-digit-group tabular-nums', className)}
      >
        {chars.map((char, index) => (
          <span
            // Position is the identity of a digit slot; the whole group re-animates on change.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="t-digit"
            data-changed={isChanged(index) ? 'true' : undefined}
          >
            {char}
          </span>
        ))}
      </span>
      <span className="sr-only">{value}</span>
    </>
  );
};

/**
 * Slot for `<Trans components={{ span: <CountdownDigits /> }}>`: the translation injects the
 * seconds as children, which then pop in on every tick.
 */
export const CountdownDigits = ({ children }: { readonly children?: ReactNode }) => (
  <DigitPopIn value={typeof children === 'string' ? children : String(children ?? '')} />
);

/** Text states swap (transitions.dev), enter phase: new text rises out of a blur. */
export const TextSwap = ({
  swapKey,
  children,
  className,
}: {
  readonly swapKey: string;
  readonly children: ReactNode;
  readonly className?: string;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isFirstRender = useRef(true);

  useLayoutEffect(() => {
    const element = ref.current;

    if (isFirstRender.current || !element) {
      // eslint-disable-next-line @silverhand/fp/no-mutation
      isFirstRender.current = false;
      return;
    }

    element.classList.add('is-enter-start');
    // Commits the start frame so removing the class transitions away from it.
    void element.offsetHeight;
    element.classList.remove('is-enter-start');
  }, [swapKey]);

  return (
    <span ref={ref} className={classNames('t-text-swap', className)}>
      {children}
    </span>
  );
};

const checkPath = 'M5 12.5 9.5 17 19 7.5';
// The path measures 19.8; the recipe asks for its length rounded up by one.
const checkPathLength = 21;

/** Success check (transitions.dev): fade, rotate upright, bob and stroke-draw on mount. */
export const SuccessCheck = ({ className }: { readonly className?: string }) => (
  <span aria-hidden className={classNames('t-success-check', className)} data-state="in">
    <svg viewBox="0 0 24 24" fill="none" className="size-6">
      <path
        d={checkPath}
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={checkPathLength}
        strokeDashoffset={checkPathLength}
      />
    </svg>
  </span>
);
